import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../services/authService';
import { User, Mail, Lock, Save, Key, Bell, ChevronDown, ChevronUp } from 'lucide-react';
import './ProfilePage.css';

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const toast = useToast();

  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [loadingProfile, setLoadingProfile] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    receive_notifications: user?.receive_notifications ?? true,
    receive_email_notifications: user?.receive_email_notifications ?? true,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [infoOpen, setInfoOpen] = useState(true);
  const [passwordOpen, setPasswordOpen] = useState(true);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoadingProfile(true);

    try {
      const response = await apiClient.patch('/auth/update_profile/', profileData);
      updateUser(response.data);
      toast.success(t('profile.updateSuccess'));
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('profile.updateError'));
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleNotificationsSubmit = async (e) => {
    e.preventDefault();
    setSavingNotifications(true);
    try {
      const response = await apiClient.patch('/auth/update_profile/', notificationPreferences);
      updateUser(response.data);
      toast.success(t('profile.notificationsSuccess'));
    } catch (error) {
      toast.error(t('profile.notificationsError'));
    } finally {
      setSavingNotifications(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error(t('profile.passwordTooShort'));
      return;
    }

    setLoadingPassword(true);

    try {
      await apiClient.post('/auth/change-password/', {
        old_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success(t('profile.passwordSuccess'));
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.error || t('profile.passwordError'));
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1 className="profile-title">
          <User size={32} />
          {t('profile.title')}
        </h1>
        <p className="profile-subtitle">{t('profile.subtitle')}</p>
      </div>

      <div className="profile-content">
        {/* Section Informations personnelles - Dropdown */}
        <div className={`profile-card ${infoOpen ? 'profile-card-open' : ''}`}>
          <button
            type="button"
            className="profile-card-header profile-card-toggle"
            onClick={() => {
              setInfoOpen(!infoOpen);
              if (!infoOpen) setPasswordOpen(false);
            }}
          >
            <div className="profile-card-title">
              <User size={20} />
              <h2>{t('profile.personalInfo')}</h2>
            </div>
            {infoOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          {infoOpen && (
            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">{t('profile.firstName')}</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={profileData.first_name}
                    onChange={handleProfileChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">{t('profile.lastName')}</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={profileData.last_name}
                    onChange={handleProfileChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={16} />
                  {t('profile.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                  className="form-input"
                />
              </div>

              <button
                type="submit"
                className="btn-save"
                disabled={loadingProfile}
              >
                <Save size={18} />
                {loadingProfile ? t('common.saving') : t('common.save')}
              </button>
            </form>
          )}
        </div>

        {/* Section Réinitialisation du mot de passe - Dropdown */}
        <div className={`profile-card ${passwordOpen ? 'profile-card-open' : ''}`}>
          <button
            type="button"
            className="profile-card-header profile-card-toggle"
            onClick={() => {
              setPasswordOpen(!passwordOpen);
              if (!passwordOpen) setInfoOpen(false);
            }}
          >
            <div className="profile-card-title">
              <Lock size={20} />
              <h2>{t('profile.changePassword')}</h2>
            </div>
            {passwordOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          {passwordOpen && (
            <form onSubmit={handlePasswordSubmit} className="profile-form">
              <div className="form-group">
                <label htmlFor="current_password">
                  <Key size={16} />
                  {t('profile.currentPassword')}
                </label>
                <input
                  type="password"
                  id="current_password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="new_password">
                  <Lock size={16} />
                  {t('profile.newPassword')}
                </label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm_password">
                  <Lock size={16} />
                  {t('profile.confirmPassword')}
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  className="form-input"
                />
              </div>

              <button
                type="submit"
                className="btn-save"
                disabled={loadingPassword}
              >
                <Key size={18} />
                {loadingPassword ? t('common.saving') : t('profile.updatePassword')}
              </button>
            </form>
          )}
        </div>
        <section className="profile-card profile-notifications" aria-labelledby="notification-preferences-title">
          <div className="profile-card-header">
            <div className="profile-card-title">
              <Bell size={20} />
              <h2 id="notification-preferences-title">{t('profile.notificationsTitle')}</h2>
            </div>
          </div>
          <form onSubmit={handleNotificationsSubmit} className="profile-form">
            <p className="notification-preferences-description">{t('profile.notificationsDescription')}</p>
            {[
              ['receive_notifications', 'receiveNotifications'],
              ['receive_email_notifications', 'receiveEmailNotifications'],
            ].map(([field, label]) => (
              <label className="notification-preference" key={field}>
                <span>{t(`profile.${label}`)}</span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={notificationPreferences[field]}
                  disabled={savingNotifications}
                  onChange={(e) => setNotificationPreferences(prev => ({ ...prev, [field]: e.target.checked }))}
                />
              </label>
            ))}
            <p className="notification-preferences-description">{t('profile.accountEmailsNotice')}</p>
            <button type="submit" className="btn-save" disabled={savingNotifications}>
              <Save size={18} />
              {savingNotifications ? t('common.saving') : t('profile.saveNotifications')}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
